/**
 * ReportTabWithPill Component - A reusable tab component that displays text with an optional status pill badge.
 * 
 * SCOPE:
 * Designed & intended to be scoped only for reports related interfaces where tabs need to show status or equivalent
 *  information alongside pill-formatted labels.
 * 
 * FEATURES:
 * - Pill defaults to ReportSecondaryStatusPill but any `PillComponent` with props `{ variant: string; text: string }`
 * is supported
 * - Pill/Badge are enabled by default, but can be set to false by callers if needed.
 * 
 * USAGE EXAMPLES:
 * SCN (Response & Notice) Report navigation tab with status indicators, where each tab element corresponds to a single
 * a party involved
 * 
 * STORIES:
 * See `ReportTabWithPill.stories.tsx`
 */

import React from 'react';
import ReportSecondaryStatusPill from '../pill/ReportSecondaryStatusPill';
import styles from './ReportTabWithPill.module.css';
import { ReportStatusPillVariant } from '../../../constants/reports'


export interface ReportTabWithPillProps {
  tabText: string;
  pillVariant: ReportStatusPillVariant;
  pillText: string;
  showBadge?: boolean;
  selected?: boolean;
  className?: string;
  PillComponent?: React.ComponentType<{ variant: string; text: string }>;
}

const ReportTabWithPill: React.FC<ReportTabWithPillProps> = ({
  tabText,
  pillVariant,
  pillText,
  showBadge = true,
  selected = false,
  className = '',
  PillComponent = ReportSecondaryStatusPill,
}) => {
  const containerClassName = `${styles.container} ${selected ? styles.selected : ''} ${className}`.trim();

  return (
    <div className={containerClassName}>
      <span className={styles.text}>{tabText}</span>
      {showBadge && (
        <PillComponent
          variant={pillVariant} 
          text={pillText} 
        />
      )}
    </div>
  );
};

export default ReportTabWithPill;